import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import AiAskBox from '../../components/AiAskBox/AiAskBox';
import { AI_PSYCHIC_COPY } from '../../data/aiPsychicCopy';
import { SITE_LANGUAGE_LANDINGS } from '../../data/siteLanguages';
import { noteHomeEvent } from '../../services/homeAnalytics';
import './AiPsychic.css';

const ORIGIN = 'https://nowtarot.com';
const ENGLISH = { code: 'en', flag: '🌐', nativeLabel: 'English' };
const LANGUAGES = [ENGLISH, ...SITE_LANGUAGE_LANDINGS];
const RTL = new Set(['ar']);

const LINK_TARGETS = {
  threeCard: '/three-card-tarot/',
  celtic: '/celtic-cross-tarot/',
  yesNo: '/yes-or-no-tarot/',
  interpreter: '/tarot-card-interpreter/',
  meanings: '/tarot-card-meanings/',
  learn: '/learn-tarot/',
};

export function aiPsychicPath(code) {
  return code === 'en' ? '/ai-psychic/' : `/ai-psychic/${code}/`;
}

function alternates() {
  return [
    { hrefLang: 'x-default', href: `${ORIGIN}/ai-psychic/` },
    ...LANGUAGES.map((language) => ({
      hrefLang: language.code,
      href: `${ORIGIN}${aiPsychicPath(language.code)}`,
    })),
  ];
}

function RichText({ template, labels }) {
  const parts = String(template || '').split(/(\{[a-zA-Z]+\})/g);
  return parts.map((part, index) => {
    const match = part.match(/^\{([a-zA-Z]+)\}$/);
    if (!match) return <React.Fragment key={index}>{part}</React.Fragment>;
    const to = LINK_TARGETS[match[1]];
    const label = labels[match[1]];
    if (!to || !label) return <React.Fragment key={index}>{part}</React.Fragment>;
    return <Link key={index} to={to}>{label}</Link>;
  });
}

const AiPsychic = () => {
  const { lang: langParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const carried = typeof location.state?.question === 'string' ? location.state.question : '';
  const draftRef = useRef(carried);
  const lang = langParam || 'en';
  const copy = AI_PSYCHIC_COPY[lang];

  useLayoutEffect(() => {
    draftRef.current = carried;
  }, [lang, carried]);

  const onAsk = useCallback(() => noteHomeEvent('ai_psychic_ask_submit', { language: lang }), [lang]);
  const onComplete = useCallback(() => noteHomeEvent('ai_psychic_ask_complete', { language: lang }), [lang]);
  const onPrompt = useCallback((prompt) => noteHomeEvent('ai_psychic_prompt_click', { prompt, language: lang }), [lang]);
  const onTool = useCallback((tool) => noteHomeEvent('ai_psychic_tool_click', { tool, language: lang }), [lang]);
  const onQuestionChange = useCallback((value) => {
    draftRef.current = value;
  }, []);

  if (langParam === 'en' || (langParam && !copy)) {
    return <Navigate to="/ai-psychic/" replace />;
  }

  const path = aiPsychicPath(lang);
  const pageUrl = `${ORIGIN}${path}`;
  const dir = RTL.has(lang) ? 'rtl' : undefined;
  const [returnBefore, returnAfter = ''] = copy.returnLine.split('{link}');
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: copy.jsonLdName,
        description: copy.description,
        url: pageUrl,
        inLanguage: lang,
        isPartOf: { '@type': 'WebSite', name: 'NowTarot', url: ORIGIN },
      },
      {
        '@type': 'FAQPage',
        inLanguage: lang,
        mainEntity: copy.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };

  const switchLanguage = (event, code) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    if (code === lang) return;
    navigate(aiPsychicPath(code), { state: { question: draftRef.current } });
  };

  return (
    <div className="aip-page" lang={lang} dir={dir}>
      <Helmet htmlAttributes={{ lang, dir: dir || 'ltr' }}>
        <title>{copy.title}</title>
        <meta name="description" content={copy.description} />
        <link rel="canonical" href={pageUrl} />
        {alternates().map((alternate) => (
          <link
            key={alternate.hrefLang}
            rel="alternate"
            hrefLang={alternate.hrefLang}
            href={alternate.href}
          />
        ))}
        <meta property="og:title" content={copy.title} />
        <meta property="og:description" content={copy.description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${ORIGIN}/og-image.png`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />

      <main>
        <section className="aip-hero" id="ai-psychic-ask">
          <div className="aip-wrap aip-ask-card">
            <h1>{copy.h1}</h1>
            <p className="aip-sub">{copy.sub}</p>
            <AiAskBox
              key={lang}
              autoFocus
              className="aip-ask"
              language={lang}
              askLabel={copy.askLabel}
              drawingLabel={copy.drawingLabel}
              placeholder={copy.placeholder}
              inputLabel={copy.inputLabel}
              suggestionsLabel={copy.suggestionsLabel}
              errorMessage={copy.error}
              prompts={copy.prompts}
              initialQuestion={carried}
              onQuestionChange={onQuestionChange}
              onAsk={onAsk}
              onComplete={onComplete}
              onPrompt={onPrompt}
            />
            <p className="aip-trust">{copy.trust}</p>
          </div>
        </section>

        <article className="aip-wrap aip-article">
          <p className="aip-lead">{copy.lead}</p>

          <section className="aip-box">
            <h2>{copy.whatTitle}</h2>
            {copy.what.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </section>

          <section className="aip-block">
            <h2>{copy.howTitle}</h2>
            <div className="aip-steps">
              {copy.steps.map((step, index) => (
                <article key={step.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="aip-box">
            <h2>{copy.futureTitle}</h2>
            {copy.future.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </section>

          <section className="aip-box">
            <h2>{copy.humanTitle}</h2>
            {copy.human.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </section>

          <section className="aip-block">
            <h2>{copy.topicsTitle}</h2>
            <div className="aip-topics">
              {copy.topics.map((topic) => (
                <article key={topic.id} className={`aip-topic aip-topic-${topic.id}`}>
                  <span>{topic.label}</span>
                  <p>{topic.text}</p>
                </article>
              ))}
            </div>
            <p className="aip-return">
              {returnBefore}
              <a href="#ai-psychic-ask">{copy.returnLink}</a>
              {returnAfter}
            </p>
          </section>

          <section className="aip-box">
            <h2>{copy.whyTitle}</h2>
            <p><RichText template={copy.why} labels={copy.links} /></p>
            <p>{copy.whyAfter}</p>
          </section>

          <h2>{copy.faqTitle}</h2>
          {copy.faq.map((item) => (
            <details key={item.q} className="aip-faq">
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}

          <h2>{copy.toolsTitle}</h2>
          <div className="aip-tools">
            {copy.tools.map((tool) => (
              <Link key={tool.to} to={tool.to} className="aip-tool" onClick={() => onTool(tool.to)}>
                <strong>{tool.title}</strong>
                <span>{tool.text}</span>
              </Link>
            ))}
          </div>

          <nav className="aip-langs" aria-label={copy.langsLabel}>
            <h2>{copy.langsLabel}</h2>
            <div className="aip-lang-list">
              {LANGUAGES.map((language) => (
                <Link
                  key={language.code}
                  to={aiPsychicPath(language.code)}
                  lang={language.code}
                  hrefLang={language.code}
                  aria-current={language.code === lang ? 'page' : undefined}
                  onClick={(event) => switchLanguage(event, language.code)}
                >
                  <span aria-hidden="true">{language.flag}</span> {language.nativeLabel}
                </Link>
              ))}
            </div>
          </nav>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default AiPsychic;
